import { Minus, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { servingCount, type Serving } from '../../shared/foods';
import { clampGrams, MAX_GRAMS, parseAmount, sizeKcal, sizeLabel, stepDown, stepUp, tenth } from '../lib/amounts';
import { kcal } from '../lib/format';
import { SizeChips, type SizeChip } from './SizeChips';

interface GramsInputProps {
  value: number;
  onChange: (grams: number) => void;
  servings?: Serving[];
  liquid?: boolean;
  label?: string;
  /** The food's calories per 100 g (or ml): each size then shows its own calories. Leave out to show sizes without them. */
  kcalPer100?: number | null;
  /** The amount logged last time, offered first as "Last time · 65 g". */
  lastGrams?: number | null;
}

/** Sizes shown before "+N more". */
const SIZE_LIMIT = 4;

/** One-tap multiples of whatever amount is set: two containers of yogurt is "1 container", then 2×. */
const MULTIPLIERS = [
  { factor: 0.5, text: '½×', label: 'Halve the amount' },
  { factor: 2, text: '2×', label: 'Double the amount' },
  { factor: 3, text: '3×', label: 'Triple the amount' },
] as const;

const matches = (grams: number, value: number) => Math.abs(grams - value) < 0.5;

/** Grams are the base unit: a stepper (5 g at a time), one-tap sizes with their calories, and quick multiples. */
export function GramsInput({ value, onChange, servings = [], liquid, label = 'Amount', kcalPer100, lastGrams }: GramsInputProps) {
  const [text, setText] = useState(String(Math.round(value)));
  useEffect(() => {
    setText(String(tenth(value)));
  }, [value]);

  const commit = (raw: string) => {
    const n = parseAmount(raw);
    if (n !== null) onChange(n);
    else setText(String(value));
  };

  const unit = liquid ? 'ml' : 'g';
  const chips: SizeChip[] = servings.map((s, i) => {
    const sizeCalories = sizeKcal(s.grams, kcalPer100);
    return {
      key: `${i}:${s.label}:${s.grams}`,
      label: sizeLabel(s, liquid),
      detail: sizeCalories === null ? null : `${kcal(sizeCalories)} kcal`,
      active: matches(s.grams, value),
      onPick: () => onChange(s.grams),
    };
  });
  if (lastGrams) {
    chips.unshift({
      key: 'last',
      label: 'Last time',
      detail: `${tenth(lastGrams)} ${unit}`,
      lead: true,
      active: matches(lastGrams, value),
      onPick: () => onChange(lastGrams),
    });
  }
  const count = servingCount(value, servings);
  return (
    <div className="stack" style={{ '--gap': '8px' } as React.CSSProperties}>
      <div className="row wrap">
        <div className="grams" role="group" aria-label={label}>
          <button type="button" className="grams__btn" aria-label="Less" onClick={() => onChange(stepDown(value))}>
            <Minus size={16} />
          </button>
          <input
            className="grams__input num"
            inputMode="decimal"
            aria-label={`${label} in ${liquid ? 'milliliters' : 'grams'}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
            }}
          />
          <span className="grams__unit">{unit}</span>
          <button type="button" className="grams__btn" aria-label="More" onClick={() => onChange(stepUp(value))}>
            <Plus size={16} />
          </button>
        </div>
        {/* A different food (say after Replace) starts with its sizes folded again. */}
        <SizeChips
          key={servings.map((s) => `${s.label}:${s.grams}`).join('|')}
          chips={chips}
          limit={SIZE_LIMIT + (lastGrams ? 1 : 0)}
          label={`${label}: sizes`}
        />
      </div>
      <div className="row wrap mults">
        <div className="servings" role="group" aria-label={`${label}: quick multiples`}>
          {MULTIPLIERS.map((m) => (
            <button
              type="button"
              key={m.factor}
              className="chip chip--sm"
              aria-label={m.label}
              disabled={m.factor > 1 ? value >= MAX_GRAMS : value <= 1}
              onClick={() => onChange(clampGrams(value * m.factor))}
            >
              {m.text}
            </button>
          ))}
        </div>
        {count && (
          <span className="small muted num" aria-live="polite">
            = {count}
          </span>
        )}
      </div>
    </div>
  );
}
